import {
	AppShell,
	Box,
	Group,
	Image,
	UnstyledButton,
	useMantineTheme
} from "@mantine/core"
import { useMantineColorScheme } from "@mantine/core"
import cPLogoDark from "@assets/svg/cp-logo-dark.svg"
import cPLogo from "@assets/svg/cp-logo.svg"
import styles from "./PageWithHeader.module.css"
import AppFooter from "@components/navigation/AppFooter"
import { useNavigate } from "react-router-dom"
import { AEMSvg } from "@components/AEMSvg"
import { AEMComponentMapper, AEMPageType } from "@AEM/models/AEMPage.model"
import { useAEMComponentData } from "@AEM/hooks/useAEMComponentData"
import { ColorScheme } from "@mantine/styles"
import { useBrandConversions } from "@hooks/useBrandConversions"

interface PageWithHeaderProps {
	children: React.ReactNode
	brands?: string[]
}

export function PageWithHeader({ children, brands }: PageWithHeaderProps) {
	const theme = useMantineTheme()
	// TODO: Santiago Ponce: Should be changed with a global aem link, we are using login images for V1
	const { componentData } = useAEMComponentData(
		AEMPageType.LOGIN,
		AEMComponentMapper.LOGIN_FORM
	)
	const subsidiaryLogos = componentData?.additionalLogos

	interface CruiseLineLogo {
		dark: React.ReactNode
		light: React.ReactNode
	}

	const brandLogos: Record<string, CruiseLineLogo> = {
		royal: {
			light: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[0]?.path
					}}
					w={86}
					h={22}
				/>
			),
			dark: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[0]?.path,
						iconColor: theme.colors.royal[6]
					}}
					w={86}
					h={22}
				/>
			)
		},
		celebrity: {
			light: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[1]?.path
					}}
					w={106}
					h={22}
				/>
			),
			dark: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[1]?.path,
						iconColor: theme.colors.royal[6]
					}}
					w={106}
					h={22}
				/>
			)
		},
		silversea: {
			light: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[2]?.path,
						iconColor: theme.colors.royal[4]
					}}
					w={92}
					h={12}
				/>
			),
			dark: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[2]?.path,
						iconColor: theme.colors.royal[6]
					}}
					w={92}
					h={12}
				/>
			)
		}
	}

	const { colorScheme } = useMantineColorScheme()
	const navigate = useNavigate()
	const logo = colorScheme === "dark" ? cPLogoDark : cPLogo
	const { urlBrandCodeToBrandName } = useBrandConversions()

	const visibleBrands = brands
		? brands
				.map((code) => urlBrandCodeToBrandName(code).toLowerCase())
				.filter(Boolean)
		: ["royal", "celebrity", "silversea"] // default brand icons

	return (
		<AppShell header={{ height: 60 }} padding="md" h="calc(100vh - 610px)">
			<Group
				px={{ base: 20, lg: 40 }}
				gap="lg"
				wrap="nowrap"
				className={styles.topBar}
				justify={visibleBrands.length === 1 ? "center" : "flex-end"}
			>
				{visibleBrands.includes("royal") &&
					brandLogos.royal[colorScheme as ColorScheme]}
				{visibleBrands.includes("celebrity") &&
					brandLogos.celebrity[colorScheme as ColorScheme]}
				{visibleBrands.includes("silversea") &&
					brandLogos.silversea[colorScheme as ColorScheme]}
			</Group>
			<AppShell.Header className={styles.appShell}>
				<Group px="md">
					<UnstyledButton
						onClick={() => navigate("/login")}
						aria-label="Navigate to CruisingPower homepage"
					>
						<Image
							src={logo}
							alt="Company Logo"
							w={{ base: 199, sm: 214 }}
							h={{ base: 28, sm: 31 }}
						/>
					</UnstyledButton>
				</Group>
			</AppShell.Header>
			<AppShell.Main p={0}>
				<Box h={"100dvh"} pt={110}>
					<Box h="100%" style={{ overflow: "auto" }}>
						{children}
						<AppShell.Footer pos="static">
							<AppFooter />
						</AppShell.Footer>
					</Box>
				</Box>
			</AppShell.Main>
		</AppShell>
	)
}
